import {useCallback, useEffect, useMemo, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import styled from "styled-components/macro";
import {Map} from "immutable";
import {Menu, MenuButton, MenuList, Tooltip, useToast} from "@chakra-ui/react";
import Header from "../../components/header";
import TopBar from "../../components/top_bar";
import SortBar from "../../components/sort_bar";
import CommunityHeader from "../../components/community_header";
import Container from "../../components/container";
import Main from '../../components/main';
import Sidebar from "../../components/sidebar";
import Footer from "../../components/footer";
import {useFeedback} from "../../store/feedback";
import Wrapper from "../../components/wrapper";
import {useCommunities, useCommunityStyle} from "../../api/communities";
import {useLoggedIn, useModal, useSettings} from "../../store";
import HeaderData from "../../components/header_data";
import Post from "../../components/post";
import {usePost} from "../../api/post";
import {decodeBase62} from "../../utils/base62";
import MarkdownField from "../../components/markdown_field";
import Button from "../../components/button";
import Comment, {PostContext} from "../../components/comment";
import {useCommentThread, useCreateComment} from "../../api/comment";
import {useQuery} from "../../utils/router";
import ErrorMessage from "../../components/error_message";
import {useBaseCommunity, usePostLink, useStoreComment} from "../../store/hooks";
import Spinner from "../../components/spinner";
import capitalize from "../../utils/capitalization";
import {useSetSuggestedSort} from "../../api/moderation";
import Signup from "../../components/signup";
import {isPseudoCommunity} from "../../store/communites";
import SignupPrompt from "../../components/signup_prompt";
import CommunityInfo from "../../components/community_info";
import CommentSortMenu from "../../components/menus/comment_sort_menu";
import useWindowDimensions from "../../utils/dimensions";
import LegacySortBar from "../../components/legacy/legacy_sort_bar";
import useHost from "../../utils/host";
import AdsenseUnderPost from "../../components/ads/adsense_under_post";
import {themecolors} from "../../utils/themecolors";
import {Bookmark, ChevronDown} from "lucide-react";

const Comments = styled.div``;
const CommentHeader = styled.div``;
const Save = styled.div``;
const Sort = styled.div``;
const Back = styled.div``;
const SetSuggestedSort = styled.div``;
const SortContainer = styled.div``;

const CommentCount = styled.span``;
const CommentOption = styled.span``;

const StyledMain = styled(Main)``;

const CommentThread = ({feedbackCh, commentId, commentSort, parentUuid, depth, postAuthor, blocked, setFirstThreadedComment}) => {
    const comment = useCommentThread (feedbackCh, commentId, commentSort, depth);
    setFirstThreadedComment(comment || false);
    return comment && !blocked?.includes(comment.author) && comment?.is_shown && <Comment comment={comment} parentUuid={parentUuid} postAuthor={postAuthor} blocked={blocked}/>;
}

const PostPage = styled(({className}) => {

    const baseCommunity = useBaseCommunity();

    const history = useHistory();
    const loggedIn = useLoggedIn();
    const params = useParams();
    const query = useQuery();
    const depth = query.d ? parseInt(query.d) : false;
    const id = useMemo(() => decodeBase62(params.uuid), [params.uuid]);
    const commentId = useMemo(() => Boolean(params.commentUuid) && decodeBase62(params.commentUuid), [params.commentUuid]);
    const {feedbackCh, busy} = useFeedback();
    const {post, comments} = usePost(feedbackCh, id, commentId ? false : query.commentSort, !commentId);
    const storeCommunity = useCommunities(feedbackCh, [post?.community])[0];
    const {community : paramCommunity} = params;
    const community = paramCommunity || baseCommunity;
    const style = useCommunityStyle(community);
    const [content, setContent] = useState('');
    const [sorting, setSorting] = useState(false);
    const link = usePostLink(post);
    const [commentMap, setCommentMap] = useState(Map);
    const expandComment = useCallback((id) => {
        setCommentMap((m) => m.set(id, true));
    }, [setCommentMap]);
    const postContext = useMemo(
        () => ({post, link, commentMap, expandComment}),
        [post, link, commentMap, expandComment]
    );
    useEffect(() => {
        let {pathname} = window.location;
        let suffix = "";
        if (params.commentUuid) {
            const index = pathname.indexOf(`/${params.commentUuid}`);
            suffix = pathname.substring(index);
            pathname = pathname.substring(0, index);
        }
        if (link && !link.startsWith(pathname)) {
            history.replace(link + suffix + window.location.search);
        }
    }, [link, params.commentUuid]);

    const handleSort = () => {
        setSorting(!sorting);
    };

    const creating = useFeedback();
    const createComment = useCreateComment(creating.channel);
    const [posting, setPosting] = useState(false);
    const modal = useModal();
    const handleCreate = async () => {
        if (posting) return;
        if (!loggedIn) {
            modal.open(Signup);
            return;
        }
        const {community, id: parentId} = post;
        try {
            setPosting(true);
            await createComment(community, parentId, 0, content);
            setContent("");
        } catch (ex) {
            // error is displayed
        }
        setPosting(false);
    };

    const toast = useToast();
    const setSuggestedSort = useSetSuggestedSort(feedbackCh);
    const handleSetSuggestedSort = async () => {
        if (!post) return;
        try {
            // set suggested sort
            setSuggestedSort(post?.id, post.comment_sort);
            toast({
                title: `Comment sort has been set to ${post.comment_sort}.`,
                status: 'success',
                isClosable: true,
            });
        } catch (ex) {
            // error is displayed
        }
    };

    const {blocked} = useSettings();

    const {width} = useWindowDimensions();

    const contextComment = useStoreComment(commentId);
    const [firstThreadedComment, setFirstThreadedComment] = useState(false);

    const handleViewAllComments = () => {
        if (!post) return;
        history.replace(`/c/${post.community}/p/${post.uuid}/x/c/`);
    };
    const handleViewParent = () => {
        if (!post) return;
        history.replace(`/c/${post.community}/p/${post.uuid}/x/c/${params.commentUuid}?d=${depth ? depth + 2 : 2}`);
    };

    const host = useHost();
    const conditionIsMet = !loggedIn && post?.ad_tier === 1 && (host === "scored.co" || host === "m.scored.co" ||
    host === "m.communities.win" || host === "communities.win");
    const adImageUrl = useMemo(() => {
        return `https://ads.scored.co/api/pixel?fmt=dsktp-under&loggedIn=${loggedIn}&width=${width}&adTier=${post?.ad_tier}&host=${host}`;
    }, [post?.ad_tier]);

    return (
        <div className={className}>
            <HeaderData ogImageUrlOverride={`https://scored.co/social/post/${post?.uuid}`} title={post ? `${post?.title} ${community !== baseCommunity ? `- ${community}` : ''}` : ''} description={post?.raw_content} image={post?.compressed_media} community={community}/>
            <Header>
                {(baseCommunity !== "TheDonald" || width <= 780) && <TopBar legacyConversion={baseCommunity === "TheDonald"}/>}
                {!isPseudoCommunity(community) &&
                    <>
                        <CommunityHeader community={community}/>
                        {baseCommunity !== "TheDonald" &&
                            <CommunityInfo desktop community={community}/>
                        }
                    </>
                }
                {baseCommunity === "TheDonald" && width > 780 && <LegacySortBar />}
            </Header>
            <SortBar community={community} />
            <Container>
                <Wrapper>
                    <StyledMain>
                        <Post
                            post={post}
                            initialExpand={'card'}
                            show_community={community === 'win' || community === 'all' || community === 'home' || community === 'trending'}
                            inPost
                        />
                    </StyledMain>

                    {conditionIsMet &&
                        <AdsenseUnderPost />
                    }
                    {post && post.ad_tier &&
                        <div style={{position: 'absolute', top: 0, left: 0, zIndex: -9}}
                             className={`sponsored ad ad-unit google-ad googleAd google-sponsored-links ads4 fullSizeAd`}>
                            <img src={adImageUrl} />
                        </div>
                    }
                    {/*{!conditionIsMet &&
                        <UnderPostAdUnit />
                    }*/}

                    <Comments className={"comments"}>

                        <CommentHeader>
                            <h2>{post && post.comments > 0 ? `${post.comments} comments` : 'Comments'}</h2>
                            <SortContainer>
                                <Menu>
                                    <MenuButton>
                                        <Sort>
                                            Sort by {capitalize(post?.comment_sort || 'Top')}
                                            <ChevronDown size={18} />
                                        </Sort>
                                    </MenuButton>
                                    <MenuList>
                                        <CommentSortMenu sort={post?.comment_sort || "top"} />
                                    </MenuList>
                                </Menu>
                            </SortContainer>
                            {post?.moderation &&
                            <Tooltip label={`Save this sort as the default for all users who view this post.`}>
                                <SetSuggestedSort onClick={handleSetSuggestedSort}>
                                    <Bookmark size={16} />
                                </SetSuggestedSort>
                            </Tooltip>}
                        </CommentHeader>

                        {!post?.is_locked && <form>
                            <MarkdownField
                                id='content' placeholder="Add your comment" minRows={5}
                                value={content} onChange={setContent}
                            />
                            <Save>
                                <ErrorMessage error={creating.error}/>
                                {creating.busy && <Spinner/>}
                                <Button onClick={handleCreate} disabled={creating.busy}>Save</Button>
                            </Save>
                        </form>}

                        {commentId &&
                            <Back>
                                <span>Viewing single comment. </span>
                                {contextComment && firstThreadedComment.in_reply_to_id === 0 &&
                                <CommentCount>No earlier comments, <CommentOption onClick={handleViewAllComments}>view
                                    all</CommentOption>.</CommentCount>
                                }
                                {contextComment && firstThreadedComment.in_reply_to_id !== 0 &&
                                <CommentCount>Jump to <CommentOption onClick={handleViewParent}>earlier
                                    comment</CommentOption>, or <CommentOption onClick={handleViewAllComments}>view
                                    all</CommentOption>.</CommentCount>
                                }
                            </Back>
                        }

                        <PostContext.Provider value={postContext}>

                            {comments && comments.map((comment) => !blocked?.includes(comment.author) && comment?.is_shown && (
                                <Comment
                                    blocked={blocked}
                                    key={comment.id}
                                    comment={comment}
                                    commentSort={post?.comment_sort}
                                    depth={1}
                                    postAuthor={post?.author}
                                />
                            ))}

                            {commentId && (
                                <CommentThread
                                    commentId={commentId}
                                    commentSort={post?.comment_sort}
                                    depth={depth}
                                    postAuthor={post?.author}
                                    blocked={blocked}
                                    setFirstThreadedComment={setFirstThreadedComment}
                                />
                            )}

                            {busy && Array(10).fill(null).map((comment, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Comment key={index}/>
                            ))}

                        </PostContext.Provider>

                    </Comments>
                </Wrapper>
                <Sidebar sidebar={style && style.sidebar} community={community} />
            </Container>
            {!loggedIn && <SignupPrompt community={community} message={`After signing up, you can vote and comment in ${community}.`} />}
            <Footer/>
        </div>
    );
})`
${Post} {
  padding: 16px 12px 16px 0;
}
${Comments} {
  margin: 6px 0;
  width: 100%;
  padding: 16px;
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
  border-radius: 4px;

}
  ${StyledMain} {
    margin-bottom: 12px;
  }
  form {
    max-width: 464px;
  }
  h2 {
    margin: 4px 0;
    font-size: 15px !important;
    font-weight: 400 !important;
    margin-bottom: 16px;
  }
  ${Save} {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 12px;
    font-size: 14px;
  }
  ${Save} button {
    margin-left: 12px;
    padding: 8px 16px;
    border-radius: 48px;
    ${({theme}) => `
        color: ${theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
        background-color: ${theme.color.name === 'dark' ? theme.dark_border : '#ececec'};
    `}
  }
  ${CommentHeader} {
    margin-bottom: 8px;
    display: flex; 
    align-items: center;
  }
  ${CommentHeader}, form {
    margin-left: 6px;
  }
  ${Sort} {
    margin-left: auto;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
    cursor: pointer;
    font-size: 14px;
    display: flex;
  }
  ${Sort} svg {
    margin-left: 4px;
  }
  ${Back} {
    font-size: 14px;
    margin-bottom: 12px;
  }
  ${CommentOption} {
    color: ${({theme}) => theme.placeholder_link_2};
    cursor: pointer;
  }
  ${SetSuggestedSort} {
    padding: 4px 8px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  ${SetSuggestedSort}:hover {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
  }
  ${SortContainer} {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
  @media only screen and (max-width: 780px) {
    ${Comments} {
      padding: 12px 8px;
    }
  }
`;

export default PostPage;
